import { useMemo, useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import { useMagasin, type Produit, type Rayon } from '../lib/client/hooks.ts'
import { proches } from '../lib/search.ts'
import { Coche } from './Icone.tsx'

/**
 * Les produits SANS RAYON, et de quoi les classer.
 *
 * Un produit sans rayon s'affiche sans emoji dans l'inventaire et n'est
 * classé nulle part en mode magasin. Jusqu'ici ça ne se corrigeait que par
 * le connecteur : l'app n'avait aucun écran pour ça, et surtout aucun
 * moyen de SAVOIR lesquels étaient concernés — une ligne nue se lit comme
 * un oubli, pas comme une tâche.
 *
 * La section disparaît quand il n'y a rien à faire. Même règle que la
 * pastille de synchronisation : l'absence EST le signal, et un « 0 produit
 * à classer » permanent est du bruit qu'on apprend à ignorer.
 */
export function AClasser() {
  const { etat } = useMagasin()
  const [classes, setClasses] = useState<Record<string, string>>({})

  const rayons = useMemo(
    () =>
      ([...etat.rayons.values()] as unknown as Rayon[])
        .filter((r) => !(r as unknown as { deletedAt?: unknown }).deletedAt)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [etat],
  )

  const { aClasser, suggestions } = useMemo(() => {
    const tous = ([...etat.catalogItems.values()] as unknown as Produit[]).filter(
      (p) => !(p as unknown as { deletedAt?: unknown }).deletedAt,
    )
    const enStock = new Set(
      [...etat.inventoryItems.values()]
        .filter((i) => !(i as unknown as { deletedAt?: unknown }).deletedAt)
        .map((i) => String((i as unknown as { catalogItemId: string }).catalogItemId)),
    )
    /*
     * Ceux qu'on a vraiment dans la cuisine d'abord : ce sont eux qu'on
     * voit nus tous les jours, donc eux qu'on a envie de régler.
     *
     * `classes` garde ceux qu'on vient de traiter DANS la liste. Sans ça
     * le produit en sortait à l'instant du tap, la liste se recomposait
     * sous le doigt et le dernier faisait disparaître la section entière —
     * on ne voyait jamais que son choix avait été pris. C'est la même
     * raison qui interdit de réordonner les lignes cochées en mode
     * magasin. La liste se vide à la prochaine ouverture des réglages.
     */
    const sans = tous
      .filter((p) => !p.rayonId || p.id in classes)
      .sort(
        (a, b) =>
          Number(enStock.has(b.id)) - Number(enStock.has(a.id)) ||
          a.name.localeCompare(b.name, 'fr'),
      )

    /*
     * La MÊME devinette qu'à la création d'un produit, réutilisée telle
     * quelle : le rayon du voisin le plus proche. Elle transforme un choix
     * parmi dix-neuf en une confirmation d'un geste, et quand elle se
     * trompe la liste complète est juste en dessous.
     */
    const voisins = tous
      .filter((p) => p.rayonId)
      .map((p) => ({ nom: p.name, search: p.search, tier: p.tier, rayonId: p.rayonId! }))
    const sugg: Record<string, string> = {}
    for (const p of sans) {
      const v = proches(voisins, p.name, 1)[0]
      if (v) sugg[p.id] = v.rayonId
    }
    return { aClasser: sans, suggestions: sugg }
  }, [etat, classes])

  if (aClasser.length === 0) return null

  function classer(produitId: string, rayonId: string) {
    magasin.dispatch('catalog.update', { id: produitId, patch: { rayonId } })
    setClasses((c) => ({ ...c, [produitId]: rayonId }))
  }

  return (
    <section className="mb-6">
      <h3 className="mb-1 text-label uppercase text-muted">
        À classer · {aClasser.length}
      </h3>
      <p className="mb-3 text-meta text-muted">
        Sans rayon, un produit s’affiche sans emoji et n’est rangé nulle part en mode magasin.
      </p>

      <ul>
        {aClasser.map((p) => {
          const fait = classes[p.id]
          const rayonFait = fait ? rayons.find((r) => r.id === fait) : null
          const suggere = suggestions[p.id]
          /*
           * Une fois classé, le produit RESTE en place au lieu de
           * disparaître. Le retirer ferait remonter le suivant sous le
           * doigt et provoquerait un faux tap — c'est la raison qui
           * interdit déjà de réordonner les lignes cochées en mode
           * magasin. La liste se vide à la prochaine ouverture.
           */
          const ordonnes = suggere
            ? [...rayons].sort(
                (a, b) => Number(b.id === suggere) - Number(a.id === suggere),
              )
            : rayons

          return (
            <li key={p.id} className="border-b border-line py-3 last:border-0">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="min-w-0 flex-1 truncate text-body text-ink">{p.name}</span>
                {rayonFait && (
                  <span className="flex shrink-0 items-center gap-1 text-meta text-muted">
                    <Coche className="size-3.5 text-accent" />
                    {rayonFait.emoji} {rayonFait.name}
                  </span>
                )}
              </div>

              {!rayonFait && (
                /* Défilement horizontal : dix-neuf rayons empilés
                   pousseraient les produits suivants hors de l'écran, et on
                   est ici pour en traiter plusieurs à la suite. */
                <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
                  {ordonnes.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => classer(p.id, r.id)}
                      className={`pressable h-10 shrink-0 rounded-full px-3 text-meta ${
                        r.id === suggere
                          ? 'bg-accent text-accent-ink'
                          : 'bg-surface-sunk text-ink'
                      }`}
                    >
                      {r.emoji} {r.name}
                    </button>
                  ))}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
